import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField
} from '@mui/material';
import api from '../../../../services/api';
import { useNotifier } from '../../../../hooks/useNotifier';
import { t } from '../../../../utils/textUtils';
import { getCurrentEngineerIds } from '../utils/formatters';

const ManageEngineersDialog = ({ open, project, projectId, onClose, onSuccess }) => {
  const { notifySuccess, notifyError } = useNotifier();
  const [options, setOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users/by-role/site_engineer');
      setOptions(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (err) {
      console.error('Error fetching site engineers:', err);
      setOptions([]);
      setError(err.response?.data?.message || 'Gagal memuat daftar insinyur lapangan.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedIds(getCurrentEngineerIds(project));
      if (options.length === 0 && !loading) fetchOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await api.put(`/projects/${projectId}`, { siteEngineerIds: selectedIds });
      notifySuccess('Insinyur lapangan berhasil diperbarui');
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating site engineers:', err);
      setError(err.response?.data?.message || 'Gagal memperbarui insinyur lapangan.');
      notifyError('Gagal memperbarui insinyur lapangan');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>{t('KELOLA INSINYUR LAPANGAN')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('Tambahkan atau hapus insinyur lapangan tanpa perlu edit proyek.')}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={(option) => option.fullName || option.username || ''}
          value={options.filter((se) => selectedIds.includes(se.id))}
          onChange={(event, newValue) => {
            setSelectedIds((newValue || []).map((item) => item.id));
          }}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('INSINYUR LAPANGAN')}
              placeholder={t('Pilih insinyur')}
              helperText={t('Bisa memilih lebih dari satu insinyur lapangan')}
            />
          )}
        />
        <FormHelperText sx={{ mt: 1 }}>
          {t('Jumlah dipilih')}: {selectedIds.length}
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('BATAL')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t('MENYIMPAN...') : t('SIMPAN')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageEngineersDialog;
